/**
 * The week itself: seven rows, a switch and two times each.
 *
 * Split out of `HoursCard` because the card is about the announcement and this
 * is about the calendar - and because the row is the fiddly part. A day is three
 * controls that have to line up on a desktop, wrap on a phone and stay readable
 * when the day is closed and two of the three are disabled.
 *
 * Seven rows rather than the chat widget's row of day chips plus one shared time
 * range, and the difference is deliberate. A phone line is what an office
 * answers, and offices close early on Fridays; one range for every ticked day
 * cannot say that. The cost is a taller card, paid back by the button that
 * copies one day's window to the rest.
 */
import { Copy } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { BusinessDay } from "../../../../lib/calls";
import { Btn } from "../../../ui";
import { Note, SelectPill, ToggleRow } from "../panel/fields";
import {
  clockLabel,
  closingOptions,
  copyWindowToOpenDays,
  firstOpenDay,
  isEmptyWindow,
  minutesFromClock,
  setDayTime,
  showsNightShiftNote,
  timeOptions,
  toggleDay,
  WEEK_ORDER,
  type OvernightMeaning,
} from "./hoursWeek";

const OPEN_TIMES = timeOptions();
const CLOSE_TIMES = closingOptions();

export function HoursSchedule({
  days,
  onChange,
  disabled,
  overnight: meaning = "shift",
}: {
  days: BusinessDay[];
  onChange: (next: BusinessDay[]) => void;
  /** The gate is off - the week is still editable, but nothing acts on it. */
  disabled?: boolean;
  /** What an end before the start means on this form (default: a night shift). A form that reads it as closed says so itself. */
  overnight?: OvernightMeaning;
}) {
  const t = useT();
  const source = firstOpenDay(days);
  const overnight = showsNightShiftNote(days, meaning);
  const empty = days.some(isEmptyWindow);

  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        {WEEK_ORDER.map((weekday) => {
          const day = days[weekday];
          if (!day) return null;
          const name = t(`settings.hours.day.${weekday}`);
          return (
            <div
              key={weekday}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[20px] border border-[var(--crm-hair)] px-4 py-2.5"
            >
              {/* The switch carries the day name, so the whole label is the hit
                  target - a name sitting beside a switch is a word people try to
                  click and nothing happens. */}
              <div className="min-w-[132px] flex-1">
                <ToggleRow
                  label={name}
                  checked={day.open}
                  disabled={disabled}
                  onChange={() => onChange(toggleDay(days, weekday))}
                />
              </div>
              <div className="flex items-center gap-2">
                <SelectPill
                  className="w-[104px]"
                  value={clockLabel(day.from)}
                  disabled={disabled || !day.open}
                  label={`${name} - ${t("settings.hours.from")}`}
                  onChange={(value) =>
                    onChange(setDayTime(days, weekday, "from", minutesFromClock(value, day.from)))
                  }
                >
                  {OPEN_TIMES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectPill>
                <span className="crm-sub shrink-0 text-[12px]">-</span>
                <SelectPill
                  className="w-[104px]"
                  value={clockLabel(day.to)}
                  disabled={disabled || !day.open}
                  label={`${name} - ${t("settings.hours.to")}`}
                  onChange={(value) =>
                    onChange(setDayTime(days, weekday, "to", minutesFromClock(value, day.to)))
                  }
                >
                  {CLOSE_TIMES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectPill>
              </div>
            </div>
          );
        })}
      </div>

      {/* Only when there is something to copy FROM and somewhere to copy TO. */}
      {source !== null ? (
        <div>
          {/* The source day is on the title rather than in the label, because
              an Estonian sentence would need it in the genitive and the day
              names are already spelled out seven times above. */}
          <Btn
            disabled={disabled}
            title={t("settings.hours.copySource", { day: t(`settings.hours.day.${source}`) })}
            onClick={() => onChange(copyWindowToOpenDays(days, source))}
          >
            <Copy className="size-3.5" />
            {t("settings.hours.copyDay")}
          </Btn>
        </div>
      ) : (
        <Note tone="warn">{t("settings.hours.noOpenDays")}</Note>
      )}

      {/* A window with no length reads as closed on the server, correctly and
          silently. A day that looks ticked and puts nobody through has to be
          said out loud here or it is never noticed. */}
      {empty ? <Note tone="warn">{t("settings.hours.emptyWindow")}</Note> : null}
      {/* And the opposite surprise: an end time before the start is a night
          shift, which is supported - but nobody sets one by accident and means
          it, so it is worth confirming on screen. */}
      {overnight ? <Note tone="info">{t("settings.hours.overnight")}</Note> : null}
    </div>
  );
}
